import { useState, useEffect } from 'react'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { 
  ArrowLeft, 
  Brain, 
  CheckCircle,
  AlertTriangle,
  Loader,
  Info,
  Zap,
  Target,
  Calendar,
  DollarSign,
  X,
  XCircle
} from 'lucide-react'
import toast from 'react-hot-toast'
import axios from 'axios'
import { API_BASE_URL } from '../constants'
import ConversationalValidation from '../components/ConversationalValidation'

import { useAuthStore } from '../store/authStore'
import { useWallet, useConnection } from '@solana/wallet-adapter-react'
import { PublicKey, Transaction, SystemProgram, TransactionInstruction, ComputeBudgetProgram } from '@solana/web3.js'
import { getAssociatedTokenAddress, TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID, createTransferInstruction, createAssociatedTokenAccountInstruction, AccountLayout } from '@solana/spl-token'
// Backend handles Solana integration
import VerifyRouteSelector from '../components/VerifyRouteSelector'
import MarketPreview from '../components/MarketPreview'
import ValidationPlanModal from '../components/ValidationPlanModal'

// Parse AI date strings to ISO format
function parseAIDateToISO(aiInterpretation: string, fallbackDate?: string): string {
  try {
    // Extract date from AI interpretation like "Bitcoin (BTC) exceed/greater than ($111000.00) by September 23, 2025 at 2:30 p.m. EST"
    const dateMatch = aiInterpretation?.match(/(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{1,2}),?\s+(\d{4})/i);
    if (dateMatch) {
      const [, month, day, year] = dateMatch;
      const monthNum = new Date(`${month} 1, 2000`).getMonth(); // Get month number
      const targetDate = new Date(parseInt(year), monthNum, parseInt(day), 23, 59, 0); // Default to end of day
      
      console.log(`📅 Parsed AI date: ${month} ${day}, ${year} → ${targetDate.toISOString()}`);
      return targetDate.toISOString();
    }
    
    // Try fallback date if provided
    if (fallbackDate && fallbackDate.includes('T')) {
      return fallbackDate;
    }
    
    // Last resort - tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(23, 59, 0, 0);
    console.warn('⚠️ Could not parse date, using tomorrow:', tomorrow.toISOString());
    return tomorrow.toISOString();
    
  } catch (error) {
    console.error('❌ Date parsing failed:', error);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(23, 59, 0, 0);
    return tomorrow.toISOString();
  }
}

// Type declaration for Phantom wallet
declare global {
  interface Window {
    solana?: {
      isPhantom?: boolean;
      connect(): Promise<{ publicKey: any }>;
      publicKey?: any;
    };
  }
}

interface CreateMarketForm {
  question: string
  description: string
  category: string
  verifyRoute: string
  resolutionCriteria: string
  bettingEndsAt: string
  resolutionTime: string
  creationStake: number
  aumTarget: number
  outcome?: number // 0=NO, 1=YES - REQUIRED
  template?: string
}

interface ValidationResult {
  approved: boolean
  approvalCount: number
  totalValidators: number
  reason?: string
  details?: any
  // Add the missing properties that the UI is trying to use
  clean_question?: string  // The cleaned up market question
  interpretation?: any  // The AI's interpretation of the market
  bettingEndsAt?: string  // ISO datetime when betting ends
  resolutionTime?: string  // ISO datetime when market resolves
  isDemo?: boolean  // Is this a demo/test market
  specialist?: string  // Which specialist handled this
  fullContext?: any  // Full context data
  timing?: {  // Timing information
    bettingEndsAt?: string
    resolutionTime?: string
    eventTime?: string
    checkDeadline?: string
  }
  aiOdds?: {  // AI-calculated market odds
    oddsYes: number
    oddsNo: number
    confidence: number
    reasoning: string
    sources: Array<{
      type: string
      source: string
      relevance: string
    }>
    researchSummary: {
      currentSentiment?: string
      keyFactors: string[]
      timelineCritical: boolean
      lastUpdated: string
    }
  }
  validations: Array<{
    validatorId: string
    decision: 'APPROVE' | 'REJECT'
    reasoning: string
    confidence: number
    dataSources?: string[]
    verificationMethod?: string
    potentialIssues?: string[]
    resolutionPlan?: string
    validationPlan?: any  // The actual executable validation plan from AI
    specialist?: string  // Which specialist handled this (sports, crypto, etc.)
  }>
}

interface ConversationState {
  status: 'analyzing' | 'needs_clarification' | 'ready_for_validation' | 'checking_duplicates' | 'invalid'
  conversationId?: string
  questions?: Array<{
    question: string
    options?: string[]
    type?: 'select' | 'text_input'
    placeholder?: string
    required?: boolean
  }>
  interpretation?: string
  detectedEntities?: any
  dataSources?: string[]
  confidence?: number
  reasoning?: string
  suggestions?: string[]
  hasDuplicates?: boolean
  similarMarkets?: Array<{
    id: number
    question: string
    similarity: number
    reasoning: string
  }>
  recommendation?: 'APPROVE' | 'REJECT' | 'MODIFY'
}

const categories = [
  { id: 'crypto', name: 'Crypto & DeFi', icon: '₿' },
  { id: 'stocks', name: 'Stocks & Equities', icon: '📈' },
  { id: 'sports', name: 'Sports', icon: '⚽' },
  { id: 'real_assets', name: 'Real Assets', icon: '🏢' },
  { id: 'world_events', name: 'World Events', icon: '🌍' },
  { id: 'politics', name: 'Politics', icon: '🏛️' },
  { id: 'entertainment', name: 'Entertainment', icon: '🎬' },
  { id: 'social', name: 'Social Media', icon: '📱' },
  { id: 'weather', name: 'Weather', icon: '🌤️' },
  { id: 'finance', name: 'Traditional Finance', icon: '💰' },
  { id: 'other', name: 'Other', icon: '🔮' }
]

const marketTemplates = [
  {
    id: 'btc_price',
    name: 'Bitcoin Price Prediction',
    category: 'crypto',
    template: {
      question: 'Will Bitcoin exceed $[PRICE] by [DATE]?',
      description: 'Market resolves based on CoinGecko closing price on the specified date.',
      verifyRoute: 'crypto_price',
      resolutionCriteria: 'Market resolves to YES if Bitcoin (BTC) closes above $[PRICE] on CoinGecko at 11:59 PM UTC on [DATE]. Otherwise resolves to NO.'
    }
  },
  {
    id: 'eth_price',
    name: 'Ethereum Price Prediction',
    category: 'crypto',
    template: {
      question: 'Will Ethereum exceed $[PRICE] by [DATE]?',
      description: 'Market resolves based on CoinGecko closing price on the specified date.',
      verifyRoute: 'crypto_price',
      resolutionCriteria: 'Market resolves to YES if Ethereum (ETH) closes above $[PRICE] on CoinGecko at 11:59 PM UTC on [DATE]. Otherwise resolves to NO.'
    }
  },
  {
    id: 'stock_price',
    name: 'Stock Price Prediction',
    category: 'finance',
    template: {
      question: 'Will [STOCK] exceed $[PRICE] by [DATE]?',
      description: 'Market resolves based on official closing price from major exchanges.',
      verifyRoute: 'stock_price',
      resolutionCriteria: 'Market resolves to YES if [STOCK] closes above $[PRICE] on any major exchange on [DATE]. Otherwise resolves to NO.'
    }
  },
  {
    id: 'sports_match',
    name: 'Sports Match Outcome',
    category: 'sports',
    template: {
      question: 'Will [TEAM1] beat [TEAM2] on [DATE]?',
      description: 'Market resolves based on official match results.',
      verifyRoute: 'sports_api',
      resolutionCriteria: 'Market resolves to YES if [TEAM1] wins the match against [TEAM2] on [DATE]. Draws resolve to NO unless specified otherwise.'
    }
  },
  {
    id: 'weather_temp',
    name: 'Weather Temperature',
    category: 'weather',
    template: {
      question: 'Will the temperature in [CITY] exceed [TEMP]°F on [DATE]?',
      description: 'Market resolves based on official weather data.',
      verifyRoute: 'weather_api',
      resolutionCriteria: 'Market resolves to YES if the maximum temperature in [CITY] exceeds [TEMP]°F on [DATE] according to Weather.gov or AccuWeather. Otherwise resolves to NO.'
    }
  },
  {
    id: 'social_followers',
    name: 'Social Media Followers',
    category: 'social',
    template: {
      question: 'Will @[USERNAME] reach [COUNT] followers by [DATE]?',
      description: 'Market resolves based on public follower count.',
      verifyRoute: 'social_media_api',
      resolutionCriteria: 'Market resolves to YES if @[USERNAME] has [COUNT] or more followers on [PLATFORM] by 11:59 PM UTC on [DATE]. Otherwise resolves to NO.'
    }
  },
  {
    id: 'custom_market',
    name: 'Custom Market',
    category: 'other',
    template: {
      question: '',
      description: 'Create your own unique prediction market with AI verification.',
      verifyRoute: 'custom_api',
      resolutionCriteria: ''
    }
  }
]

export default function CreateMarketPage() {
  const [step, setStep] = useState(1)
  const [isValidating, setIsValidating] = useState(false)
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null)
  const [showResolutionModal, setShowResolutionModal] = useState(false)
  const [showExamplesModal, setShowExamplesModal] = useState(false) // New modal for examples
  const [showOddsReasoningModal, setShowOddsReasoningModal] = useState(false) // AI Odds reasoning
  const [conversation, setConversation] = useState<boolean>(false) // Track if user wants AI conversation
  const [useCustomOdds, setUseCustomOdds] = useState(false) // Toggle AI vs custom odds
  const [customOddsYes, setCustomOddsYes] = useState(50) // Custom YES percentage (will be overridden by AI recommendation)
  const [creationMode, setCreationMode] = useState<'manual' | 'news'>('manual') // Toggle between manual and news
  const [newsProgress, setNewsProgress] = useState<any>(null) // Track news creation progress
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuthStore()
  const { connected, publicKey } = useWallet()
  const { connection } = useConnection()
  // const { createMarket, isLoading: contractLoading, tokenMint } = usePredictionMarket()

  // Data sources for auto-generating resolution criteria
  const dataSources = {
    crypto: { source: "CoinGecko & CoinMarketCap" },
    stocks: { source: "Yahoo Finance & Alpha Vantage" },
    finance: { source: "Yahoo Finance & Alpha Vantage" },
    sports: { source: "ESPN & Official League APIs" },
    real_assets: { source: "Market Data APIs & Official Sources" },
    world_events: { source: "Reuters, AP News & Official Sources" },
    weather: { source: "Weather.gov & AccuWeather" },
    social: { source: "Twitter, Instagram & YouTube APIs" },
    entertainment: { source: "Box Office Mojo & Entertainment APIs" },
    politics: { source: "Official Election Results & Government Sources" },
    news: { source: "Reuters, AP News & Google News" },
    other: { source: "Google Search & Multiple Sources" }
  }

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<CreateMarketForm>({
    defaultValues: {
      creationStake: 5,
      aumTarget: 100,
      category: 'other', // Start neutral, let user/AI choose
      verifyRoute: 'manual', // Start neutral, let user/AI choose
      outcome: undefined // Explicitly undefined until user selects
    }
  })

  const watchedValues = watch()

  // Initialize custom odds slider to AI recommendation when validation completes
  useEffect(() => {
    if (validationResult?.aiOdds) {
      const aiYesPercent = Math.round((validationResult.aiOdds.oddsYes || 0.5) * 100);
      setCustomOddsYes(aiYesPercent);
      setUseCustomOdds(false); // Reset to AI recommendation
      console.log(`🎲 Slider initialized to AI recommendation: ${aiYesPercent}% YES`);
    }
  }, [validationResult?.aiOdds]);

  // Auto-analyze question to set category and verify route
  const autoAnalyzeQuestion = () => {
    const question = (watchedValues.question || '').toLowerCase();
    console.log('🧠 AI analyzing question:', question);
    
    // Auto-detect category based on keywords
    if (question.includes('tweet') || question.includes('post') || question.includes('elon') || 
        question.includes('social') || question.includes('instagram') || question.includes('tiktok')) {
      setValue('category', 'social');
      setValue('verifyRoute', 'social_media');
      console.log('🔍 Auto-detected: SOCIAL MEDIA market');
    } else if (question.includes('bitcoin') || question.includes('btc') || question.includes('ethereum') || 
               question.includes('crypto') || question.includes('solana') || question.includes('bonk')) {
      setValue('category', 'crypto');
      setValue('verifyRoute', 'crypto_price');
      console.log('🔍 Auto-detected: CRYPTO market');
    } else if (question.includes('stock') || question.includes('tesla') || question.includes('apple') || 
               question.includes('nasdaq') || question.includes('spy')) {
      setValue('category', 'finance');
      setValue('verifyRoute', 'stock_price');
      console.log('🔍 Auto-detected: FINANCE market');
    } else if (question.includes('temperature') || question.includes('rain') || question.includes('snow') || 
               question.includes('weather') || question.includes('hurricane')) {
      setValue('category', 'weather');
      setValue('verifyRoute', 'weather_data');
      console.log('🔍 Auto-detected: WEATHER market');
    } else {
      setValue('category', 'other');
      setValue('verifyRoute', 'manual');
      console.log('🔍 Auto-detected: OTHER market (manual verification)');
    }
  };

  // Auto-populate market close date based on AI analysis of the question
  const autoSetCloseDate = () => {
    const question = watchedValues.question || ''
    console.log('🔍 Parsing question for date:', question);
    let suggestedDate = ''

    // Extract specific dates from question using regex patterns
    // Pattern 1: MM/DD/YYYY (e.g., "12/31/2025") - Make sure to reset regex
    const datePattern1 = /(\d{1,2})\/(\d{1,2})\/(\d{4})/
    const match1 = question.match(datePattern1)
    console.log('🔍 Date match result:', match1);
    
    // Pattern 2: Month DD, YYYY (e.g., "December 31, 2025")  
    const datePattern2 = /(january|february|march|april|may|june|july|august|september|october|november|december)\s+(\d{1,2}),?\s+(\d{4})/gi
    const match2 = datePattern2.exec(question)
    
    // Pattern 3: DD/MM/YYYY or similar variations
    const datePattern3 = /by\s+(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/gi
    const match3 = datePattern3.exec(question)

    if (match1) {
      // MM/DD/YYYY format
      const [, month, day, year] = match1
      suggestedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    } else if (match2) {
      // Month name format
      const [, monthName, day, year] = match2
      const months = {
        january: '01', february: '02', march: '03', april: '04',
        may: '05', june: '06', july: '07', august: '08', 
        september: '09', october: '10', november: '11', december: '12'
      }
      const month = months[monthName.toLowerCase()]
      if (month) {
        suggestedDate = `${year}-${month}-${day.padStart(2, '0')}`
      }
    } else if (match3) {
      // Alternative date format
      const [, day, month, year] = match3
      suggestedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    } else {
      // Fallback: Default to 30 days from now
      const defaultDate = new Date()
      defaultDate.setDate(defaultDate.getDate() + 30)
      suggestedDate = defaultDate.toISOString().split('T')[0]
    }

    // Validate the date is in the future (any time, even 10 minutes)
    const now = new Date()
    const endDate = new Date(suggestedDate)
    
    if (endDate <= now) {
      // If parsed date is in the past, add one year to make it future
      endDate.setFullYear(endDate.getFullYear() + 1)
      suggestedDate = endDate.toISOString().split('T')[0]
    }

    if (suggestedDate) {
      setValue('bettingEndsAt', suggestedDate)
      setValue('resolutionTime', suggestedDate) // Same date for resolution
      console.log('📅 Auto-set date:', suggestedDate);
    }
  }

  // Handle template selection
  const applyTemplate = (templateId: string) => {
    const template = marketTemplates.find(t => t.id === templateId)
    if (template) {
      setValue('template', templateId)
      setValue('category', template.category)
      setValue('question', template.template.question)
      setValue('description', template.template.description)
      setValue('verifyRoute', template.template.verifyRoute)
      setValue('resolutionCriteria', template.template.resolutionCriteria)
    }
  }


  if (!connected) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Brain className="w-16 h-16 text-primary-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Authentication Required</h2>
          <p className="text-gray-300 mb-6">Please connect your wallet to create markets</p>
          <button className="btn-primary" onClick={() => navigate('/')}>
            Go Home
          </button>
        </div>
      </div>
    )
  }

  const onSubmit = async (data: CreateMarketForm) => {
    try {
      setIsValidating(true)
      
      // CRITICAL: Check if market was AI approved
      if (!validationResult?.approved) {
        toast.error('Market must be AI-approved before creation! Go back to Step 2.')
        setIsValidating(false)
        return
      }
      
      console.log('✅ AI APPROVED MARKET - PROCEEDING WITH BLOCKCHAIN CREATION');
      console.log('🤖 AI Validation Details:', validationResult.validations?.[0]);
      
      // Generate a unique market ID
      const marketId = Date.now() // Simple timestamp-based ID
      
      // CRITICAL: Blockchain needs RESOLUTION time (when to trigger validation)
      // NOT betting deadline (when betting stops)
      // Use AI's ISO datetime, NOT the human-readable form field!
      const resolutionTimeToUse = validationResult?.timing?.resolutionTime || validationResult?.resolutionTime || data.bettingEndsAt;
      const closesAtUnix = Math.floor(new Date(resolutionTimeToUse).getTime() / 1000)
      console.log('🎯 BLOCKCHAIN RESOLUTION TRIGGER TIME:', resolutionTimeToUse);
      console.log('🎯 This is when blockchain will emit resolution event');
      
      // Use AI's resolution criteria from the conversation
      const aiValidation = validationResult.validations?.[0];
      const autoResolutionCriteria = aiValidation?.resolutionPlan || 
        `AI will verify this market using ${dataSources[data.category as keyof typeof dataSources]?.source || "multiple data sources"} and resolve based on the market question: "${data.question}"`
      
      // Validate required bet selection
      if (data.outcome !== 0 && data.outcome !== 1) {
        toast.error('You must select YES or NO to create the pool')
        return
      }

      // CRITICAL: Recalculate timing for demo markets to ensure it's in the future
      let finalBettingEndsAt = data.bettingEndsAt;
      let finalResolutionTime = validationResult?.timing?.resolutionTime || validationResult?.resolutionTime || data.bettingEndsAt;
      
      // Check if this is a demo market  
      console.log('🔍 Demo detection check:');
      console.log('  - validationResult.isDemo:', validationResult?.isDemo);
      console.log('  - validationResult.specialist:', validationResult?.specialist);
      console.log('  - fullContext.specialist:', validationResult?.fullContext?.specialist);
      
      const isDemo = validationResult?.isDemo === true || 
                     validationResult?.specialist === 'demo_sports' || 
                     validationResult?.specialist === 'demo_crypto' ||
                     validationResult?.fullContext?.specialist?.includes('demo');
      
      console.log('  - IS DEMO?', isDemo);
      
      if (isDemo) {
        console.log('🧪 DEMO MARKET: Recalculating timing to ensure future dates');
        finalBettingEndsAt = new Date(Date.now() + 60 * 1000).toISOString(); // NOW + 30 seconds
        finalResolutionTime = new Date(Date.now() + 90 * 1000).toISOString(); // NOW + 60 seconds
        console.log('📅 Recalculated betting ends:', finalBettingEndsAt);
        console.log('📅 Recalculated resolution:', finalResolutionTime);
      }
      
      const bettingEndTimestamp = new Date(finalBettingEndsAt).getTime();
      const currentTimestamp = Date.now();
      
      console.log('📅 FINAL DATE CHECK:');
      console.log('📅 Betting ends at:', finalBettingEndsAt);
      console.log('📅 Parsed as:', new Date(finalBettingEndsAt).toLocaleString());
      console.log('📅 Unix timestamp:', Math.floor(bettingEndTimestamp / 1000));
      console.log('📅 Current Unix:', Math.floor(currentTimestamp / 1000));
      console.log('📅 Is future?', bettingEndTimestamp > currentTimestamp ? '✅ YES' : '❌ NO - WILL FAIL!');
      
      if (bettingEndTimestamp <= currentTimestamp) {
        if (isDemo) {
          console.log('🧪 DEMO MARKET: Date in past but allowed for demos');
        } else {
          console.error('❌ STOPPING: Market deadline is in the past!');
          toast.error('Market closing date must be in the future! Check the dates.');
          setIsValidating(false);
          return;
        }
      }
      
      // Update data with recalculated times for demo
      if (isDemo) {
        data.bettingEndsAt = finalBettingEndsAt;
      }

      // First call the backend API for validation
      const payload = {
        ...data,
        marketId,
        outcome: data.outcome, // Include the bet choice
        creationStake: Number(data.creationStake), // Ensure it's a number
        aumTarget: Number(data.aumTarget), // Ensure it's a number
        walletAddress: publicKey?.toString() || 'unknown', // Send actual wallet address!
        resolutionCriteria: autoResolutionCriteria,
        // USE THE DATES WE ALREADY SET CORRECTLY - DON'T PARSE AGAIN!
        bettingEndsAt: data.bettingEndsAt, // Already set correctly from AI response
        resolutionTime: resolutionTimeToUse // Use the ISO datetime we calculated above!
      }

      // First store in database WITH validation details
      // CRITICAL: If user adjusted odds, override AI odds
      const finalValidationDetails = { ...validationResult };
      if (useCustomOdds && validationResult?.aiOdds) {
        console.log(`🎲 Using CUSTOM odds: ${customOddsYes}% YES / ${100 - customOddsYes}% NO`);
        finalValidationDetails.aiOdds = {
          ...validationResult.aiOdds,
          oddsYes: customOddsYes / 100,
          oddsNo: (100 - customOddsYes) / 100,
          reasoning: `Creator adjusted odds from AI recommendation. ${validationResult.aiOdds.reasoning}`,
          confidence: validationResult.aiOdds.confidence - 10 // Lower confidence for manual adjustment
        };
      } else {
        console.log(`🎲 Using AI odds: ${Math.round((validationResult?.aiOdds?.oddsYes || 0.5) * 100)}% YES`);
      }
      
      const payloadWithValidation = {
        ...payload,
        validationDetails: finalValidationDetails // Include validation with final odds
      };
      
      const response = await axios.post('/api/markets', payloadWithValidation)
      
      if (response.data.success) {
        // Now make REAL blockchain call
        try {
          toast.loading('Creating pool on blockchain...', { id: 'blockchain' });
          
          // User-friendly note about Phantom's simulation warning
          toast('💡 Note: Phantom simulation may show warnings, but transaction often succeeds anyway. Check the result after confirming.', {
            duration: 6000,
            icon: 'ℹ️'
          });
          
          // REAL contract call using your connected wallet



          if (!(window as any).solana || !(window as any).solana.isPhantom) {
            throw new Error('Phantom wallet not found');
          }

          // Using connection from useConnection() hook
          const programId = new PublicKey('2Jso3cMEDPjQYx1nKsWPsCkJzzAj8kpVsTwq4PSRPGV1');
          const tokenMint = new PublicKey('5mbkMp8x4aZ9ntsVPPnVUFekiE1joGU4gSANfsTtiiaT');
          
          // Connect to your wallet
          await (window as any).solana.connect();
          const userWallet = (window as any).solana.publicKey;
          
          console.log('🚀 REAL CONTRACT CALL');
          console.log('💰 Your wallet:', userWallet.toString());
          console.log('🎯 Contract:', programId.toString());
          console.log('💎 Tokens:', tokenMint.toString());
          
          // BYPASS IDL - Make direct contract call
          
          console.log('🚀 CREATING REAL MARKET ON BLOCKCHAIN');
          
          // Get your token account
          const userTokenAccount = await getAssociatedTokenAddress(tokenMint, userWallet);
          
          // Check if token account exists, create if not
          const tokenAccountInfo = await connection.getAccountInfo(userTokenAccount);
          if (!tokenAccountInfo) {
            console.log('🔧 Creating token account for user...');
            const createAtaIx = createAssociatedTokenAccountInstruction(
              userWallet,
              userTokenAccount,
              userWallet,
              tokenMint
            );
            const createAtaTx = new Transaction().add(createAtaIx);
            const { blockhash } = await connection.getLatestBlockhash();
            createAtaTx.recentBlockhash = blockhash;
            createAtaTx.feePayer = userWallet;
            
            const signedAtaTx = await (window as any).solana.signTransaction(createAtaTx);
            const ataSig = await connection.sendRawTransaction(signedAtaTx.serialize());
            await connection.confirmTransaction(ataSig, 'confirmed');
            console.log('✅ Token account created!');
            toast.success('Token account initialized!');
          }
          
          // Derive the config PDA
          const [configPDA] = PublicKey.findProgramAddressSync(
            [Buffer.from("config")],
            programId
          );
          
          // Derive the market PDA (using first 6 bytes of market_id)
          // USE THE SAME MARKET ID FROM ABOVE - NOT A NEW ONE!!!
          const blockchainMarketId = response.data.marketId || marketId; // Use the ID from backend/database
          const marketIdBytes = new Uint8Array(8);
          new DataView(marketIdBytes.buffer).setBigUint64(0, BigInt(blockchainMarketId), true); // little-endian
          const [marketPDA] = PublicKey.findProgramAddressSync(
            [Buffer.from("market"), marketIdBytes.slice(0, 6)],
            programId
          );
          
          // Create market instruction
          const discriminator = Buffer.from([103, 226, 97, 235, 200, 188, 251, 254]); // create_market
          
          // Helper to encode string with length prefix (Anchor format)
          const encodeString = (str: string) => {
            const bytes = new TextEncoder().encode(str);
            const lengthBytes = new Uint8Array(4);
            new DataView(lengthBytes.buffer).setUint32(0, bytes.length, true); // little-endian
            return Buffer.concat([lengthBytes, bytes]);
          };
          
          // Debug the values before encoding
          console.log('🔍 Blockchain args debug:', {
            blockchainMarketId,
            bettingEndsAt: data.bettingEndsAt,
            bettingEndsAtDate: new Date(data.bettingEndsAt),
            timestamp: new Date(data.bettingEndsAt).getTime(),
            unixTimestamp: Math.floor(new Date(data.bettingEndsAt).getTime() / 1000),
            creationStake: data.creationStake,
            aumTarget: data.aumTarget
          });
          
          // Use the date from the backend response if data.bettingEndsAt is undefined
          const bettingEndDate = data.bettingEndsAt || payload.bettingEndsAt || '2025-10-31T23:59:00Z';
          const closesAtUnix = Math.floor(new Date(bettingEndDate).getTime() / 1000);
          const nowUnix = Math.floor(Date.now() / 1000);
          
          console.log('📅 Using betting end date:', bettingEndDate);
          console.log('⏰ Unix timestamp:', closesAtUnix);
          console.log('🕐 Current time Unix:', nowUnix);
          console.log('✅ Is future date?', closesAtUnix > nowUnix ? 'YES' : 'NO - WILL FAIL!');
          
          if (isNaN(closesAtUnix)) {
            throw new Error(`Invalid betting end date: ${bettingEndDate} (from data: ${data.bettingEndsAt}, payload: ${payload.bettingEndsAt})`);
          }
          
          if (closesAtUnix <= nowUnix) {
            console.error('❌ BLOCKCHAIN WILL REJECT: Market closing date is in the past!');
            console.error(`❌ Closing: ${new Date(closesAtUnix * 1000).toISOString()}`);
            console.error(`❌ Current: ${new Date(nowUnix * 1000).toISOString()}`);
            toast.error('Market closing date must be in the future!');
            throw new Error('Market closing date is in the past - blockchain will reject this');
          }
          
          // CRITICAL DEBUG: Log what we're sending
          const validationSource = (data.verifyRoute || 'manual').substring(0, 50);
          const validationCriteria = autoResolutionCriteria.substring(0, 50);
          
          console.log('📦 BLOCKCHAIN ARGUMENTS DEBUG:');
          console.log('  market_id:', blockchainMarketId);
          console.log('  closes_at_unix:', closesAtUnix, '→', new Date(closesAtUnix * 1000).toISOString());
          console.log('  question:', data.question.substring(0, 140), `(${data.question.length} chars)`);
          console.log('  creator_stake:', data.creationStake);
          console.log('  creator_outcome:', data.outcome, '→', data.outcome === 1 ? 'YES' : 'NO');
          console.log('  aum_target:', data.aumTarget || 100);
          console.log('  validation_source:', validationSource, `(${validationSource.length} chars)`);
          console.log('  validation_criteria:', validationCriteria, `(${validationCriteria.length} chars)`);
          console.log('  Current Unix time:', Math.floor(Date.now() / 1000));
          console.log('  Time until closes:', closesAtUnix - Math.floor(Date.now() / 1000), 'seconds');
          
          // Encode arguments (WITH creator_outcome!)
          const args = Buffer.concat([
            discriminator,
            Buffer.from(new Uint8Array(new BigUint64Array([BigInt(blockchainMarketId)]).buffer)), // market_id - USE THE SAME ONE!
            Buffer.from(new Uint8Array(new BigInt64Array([BigInt(closesAtUnix)]).buffer)), // closes_at
            encodeString(data.question.substring(0, 140)), // question (max 140 chars)
            Buffer.from(new Uint8Array(new BigUint64Array([BigInt(data.creationStake)]).buffer)), // creator_stake
            Buffer.from([data.outcome]), // creator_outcome (YES = 1, NO = 0) - WHAT THEY'RE BETTING ON!
            Buffer.from(new Uint8Array(new BigUint64Array([BigInt(data.aumTarget || 100)]).buffer)), // aum_target
            encodeString(validationSource), // validation_source (max 50)
            encodeString(validationCriteria), // validation_criteria (max 50)
          ]);
          
          console.log('📦 Total instruction data size:', args.length, 'bytes');
          
          // Derive vault authorities
          const [yesVaultAuthority] = PublicKey.findProgramAddressSync(
            [Buffer.from("yes_vault_auth"), marketPDA.toBuffer()],
            programId
          );
          const [noVaultAuthority] = PublicKey.findProgramAddressSync(
            [Buffer.from("no_vault_auth"), marketPDA.toBuffer()],
            programId
          );
          
          // Derive vault token accounts
          const yesVault = await getAssociatedTokenAddress(tokenMint, yesVaultAuthority, true);
          const noVault = await getAssociatedTokenAddress(tokenMint, noVaultAuthority, true);
          
          // Get user's token account (NOW REQUIRED!)
          // userTokenAccount is already declared above on line 349
          
          // CHECK IF USER HAS TOKEN ACCOUNT AND BALANCE
          const userTokenAccountInfo = await connection.getAccountInfo(userTokenAccount);
          if (!userTokenAccountInfo) {
            console.error('❌ USER HAS NO TOKEN ACCOUNT!');
            toast.error('You need custom tokens to create markets. Please get tokens first.');
            return;
          }
          
          // Parse token account to check balance
          const parsedAccount = AccountLayout.decode(userTokenAccountInfo.data);
          const balance = Number(parsedAccount.amount);
          
          console.log('💰 User token balance:', balance);
          console.log('💰 Required for market:', data.creationStake);
          
          if (balance < data.creationStake) {
            console.error('❌ INSUFFICIENT TOKENS!');
            toast.error(`Insufficient balance. You have ${balance} tokens but need ${data.creationStake}`);
            return;
          }
          
          // Derive creator position PDA (for claim later)
          const [creatorPositionPDA] = PublicKey.findProgramAddressSync(
            [Buffer.from("position"), marketPDA.toBuffer(), userWallet.toBuffer()],
            programId
          );
          
          // Build accounts array for create_market (must match the Rust struct order)
          const accounts = [
            { pubkey: configPDA, isSigner: false, isWritable: true },
            { pubkey: marketPDA, isSigner: false, isWritable: true },
            { pubkey: yesVaultAuthority, isSigner: false, isWritable: false },
            { pubkey: noVaultAuthority, isSigner: false, isWritable: false },
            { pubkey: yesVault, isSigner: false, isWritable: true },
            { pubkey: noVault, isSigner: false, isWritable: true },
            { pubkey: tokenMint, isSigner: false, isWritable: false },
            { pubkey: userWallet, isSigner: true, isWritable: true },
            { pubkey: creatorPositionPDA, isSigner: false, isWritable: true }, // CREATOR POSITION - NEW!
            { pubkey: userTokenAccount, isSigner: false, isWritable: true }, // USER'S TOKENS WILL BE TAKEN FROM HERE!
            { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
            { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
            { pubkey: ASSOCIATED_TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
            { pubkey: new PublicKey("SysvarRent111111111111111111111111111111111"), isSigner: false, isWritable: false },
          ];
          
          const createMarketInstruction = new TransactionInstruction({
            keys: accounts,
            programId: programId,
            data: args,
          });
          
          // Create approval instruction for the market
          const approveDiscriminator = Buffer.from([139, 230, 166, 16, 198, 220, 79, 146]); // approve_test
          const approveInstruction = new TransactionInstruction({
            keys: [
              { pubkey: marketPDA, isSigner: false, isWritable: true },   // market account
              { pubkey: userWallet, isSigner: true, isWritable: false }    // authority
            ],
            programId: programId,
            data: approveDiscriminator
          });
          
          // Add compute budget to help with simulation
          
          const computeUnitLimit = ComputeBudgetProgram.setComputeUnitLimit({ 
            units: 400000 // High compute units for clean simulation
          });
          
          const computeUnitPrice = ComputeBudgetProgram.setComputeUnitPrice({ 
            microLamports: 10000 // Higher priority fee for better simulation
          });
          
          // COMBINE ALL INSTRUCTIONS IN ONE TRANSACTION!
          const transaction = new Transaction()
            .add(computeUnitLimit)          // First: Set compute budget
            .add(computeUnitPrice)          // Second: Set priority fee
            .add(createMarketInstruction)   // Third: Create market and stake tokens
            .add(approveInstruction);       // Fourth: Approve market for betting
            
          transaction.feePayer = userWallet;
          const { blockhash } = await connection.getLatestBlockhash();
          transaction.recentBlockhash = blockhash;
          
          console.log('🎯 OPENING WALLET TO CREATE & APPROVE MARKET WITH', data.creationStake, 'TOKENS ON', data.outcome === 1 ? 'YES' : 'NO');
          
          // THIS WILL OPEN YOUR WALLET - ONE TRANSACTION DOES EVERYTHING!
          const signedTransaction = await (window as any).solana.signTransaction(transaction);
          console.log('✅ Transaction signed by wallet!');
          
          const signature = await connection.sendRawTransaction(signedTransaction.serialize());
          
          console.log('📡 Transaction sent:', signature);
          console.log('⏳ Waiting for confirmation (may take up to 30 seconds on devnet)...');
          
          // CRITICAL: Use polling instead of WebSocket for more reliable confirmation
          let confirmed = false;
          let attempts = 0;
          const maxAttempts = 60; // 60 attempts * 1 second = 60 seconds max
          
          while (!confirmed && attempts < maxAttempts) {
            attempts++;
            try {
              const status = await connection.getSignatureStatus(signature);
              console.log(`⏳ Attempt ${attempts}: Status =`, status?.value?.confirmationStatus);
              
              if (status?.value?.confirmationStatus === 'confirmed' || status?.value?.confirmationStatus === 'finalized') {
                confirmed = true;
                console.log('✅ Transaction confirmed!');
                break;
              }
              
              if (status?.value?.err) {
                console.error('❌ Transaction failed:', status.value.err);
                throw new Error(`Transaction failed: ${JSON.stringify(status.value.err)}`);
              }
            } catch (e) {
              console.log(`⚠️ Attempt ${attempts} failed to get status:`, e);
            }
            
            // Wait 1 second before next attempt
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
          
          if (!confirmed) {
            // Transaction might still have succeeded, check explorer
            console.warn('⚠️ Confirmation timeout, but transaction may have succeeded!');
            console.log('🔗 CHECK EXPLORER:', `https://explorer.solana.com/tx/${signature}?cluster=devnet`);
            
            // One last check with finalized commitment
            const finalStatus = await connection.getSignatureStatus(signature);
            if (finalStatus?.value && !finalStatus.value.err) {
              console.log('✅ Transaction found in final check!');
              confirmed = true;
            }
          }
          
          const confirmation = { value: { err: !confirmed ? 'Timeout' : null } };
          
          console.log('📊 Confirmation result:', confirmation);
          
          if (confirmation.value.err) {
            console.error('❌ TRANSACTION FAILED ON BLOCKCHAIN:', confirmation.value.err);
            throw new Error(`Blockchain transaction failed: ${JSON.stringify(confirmation.value.err)}`);
          }
          
          // ADDITIONAL CHECK: Fetch the actual transaction to see program logs
          const txDetails = await connection.getTransaction(signature, {
            commitment: 'confirmed',
            maxSupportedTransactionVersion: 0
          });
          
          console.log('📋 Transaction details:', txDetails);
          
          if (txDetails?.meta?.err) {
            console.error('❌ TRANSACTION FAILED (from meta):', txDetails.meta.err);
            console.error('📜 Program logs:', txDetails.meta.logMessages);
            throw new Error(`Blockchain transaction failed: ${JSON.stringify(txDetails.meta.err)}`);
          }
          
          if (!txDetails) {
            console.error('⚠️ Could not retrieve transaction details');
          }
          
          console.log('✅ MARKET CREATED & APPROVED ON BLOCKCHAIN:', signature);
          console.log('✅ MARKET ID:', blockchainMarketId);
          console.log('🔗 VERIFY ON EXPLORER:', `https://explorer.solana.com/tx/${signature}?cluster=devnet`);
          console.log('🔗 MARKET ACCOUNT:', `https://explorer.solana.com/address/${marketPDA.toString()}?cluster=devnet`);
          
          // VERIFY MARKET ACTUALLY EXISTS ON BLOCKCHAIN
          // Since transaction succeeded, market MUST exist - navigate immediately
          console.log('✅ Transaction succeeded - market created on blockchain!');
          
          // Show success with blockchain links
          toast.success(
            <div className="space-y-2">
              <p className="font-bold">✅ Market Created on Blockchain!</p>
              <div className="space-y-1 text-xs">
                <a 
                  href={`https://explorer.solana.com/tx/${signature}?cluster=devnet`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-blue-300 hover:text-blue-200 underline"
                >
                  View Transaction →
                </a>
                <a 
                  href={`https://explorer.solana.com/address/${marketPDA.toString()}?cluster=devnet`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-blue-300 hover:text-blue-200 underline"
                >
                  View Market Account →
                </a>
              </div>
            </div>,
            { id: 'blockchain', duration: 10000 }
          );
          
          // Navigate to market immediately (transaction confirmed = market exists)
          navigate(`/markets/${blockchainMarketId}`);
          
          // Background verification for logging purposes only
          setTimeout(async () => {
            try {
              // Use 'finalized' commitment to bypass RPC caching
              const marketAccount = await connection.getAccountInfo(marketPDA, 'finalized');
              if (marketAccount) {
                console.log('✅ BACKGROUND VERIFICATION: Market exists on blockchain!');
                console.log('📊 Market account data length:', marketAccount.data.length);
              } else {
                console.warn('⚠️ Market not found with finalized commitment - RPC caching issue, but tx succeeded');
              }
            } catch (verifyError) {
              console.warn('⚠️ Background verification failed (non-critical):', verifyError);
            }
          }, 5000); // Wait 5 seconds for finalized commitment
          
        } catch (error: any) {
          console.error('❌ BLOCKCHAIN FAILED:', error);
          console.error('❌ Error type:', typeof error);
          console.error('❌ Error message:', error?.message);
          console.error('❌ Error stack:', error?.stack);
          console.error('❌ Full error object:', JSON.stringify(error, null, 2));
          
          // Check for Phantom internal error (-32603)
          if (error?.code === -32603 || error?.message?.includes('Unexpected error')) {
            toast.error(
              <div>
                <strong>🔄 Phantom Wallet Connection Issue</strong>
                <p style={{ marginTop: '8px', fontSize: '14px' }}>
                  Please try these steps:
                </p>
                <ol style={{ marginLeft: '16px', fontSize: '13px' }}>
                  <li>Lock/sign out of your Phantom wallet</li>
                  <li>Unlock/sign back in</li>
                  <li>Click "Connect Wallet" again</li>
                  <li>Retry creating the market</li>
                </ol>
              </div>,
              { 
                id: 'blockchain',
                duration: 10000
              }
            );
          } else {
            toast.error(`BLOCKCHAIN FAILED: ${error?.message || error?.toString() || 'Unknown error'}`, { id: 'blockchain' });
          }
          
          // DELETE THE DATABASE MARKET SINCE BLOCKCHAIN FAILED
          try {
            console.log('🗑️ Deleting database market since blockchain failed...');
            await axios.delete(`/api/markets/${response.data.marketId}`);
            console.log('✅ Database market deleted after blockchain failure');
          } catch (deleteError) {
            console.error('❌ Failed to cleanup database after blockchain failure:', deleteError);
          }
          
          throw error;
        }
      } else {
        toast.error('Failed to create pool')
      }
      
    } catch (error: any) {
      console.error('Market creation error:', error)
      toast.error(error.message || 'Failed to create market on Solana')
    } finally {
      setIsValidating(false)
    }
  }

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-8">
            {/* MARKET QUESTION + AI CHAT - FIRST ON PAGE */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-white/15 via-[#009245]/10 to-white/15 rounded-2xl blur-xl"></div>
              <div className="relative card p-8 border-2 border-white/30 shadow-2xl shadow-white/20 space-y-6">
                {/* Market Question Input - ONLY in manual mode */}
                {creationMode === 'manual' && (
                <div>
                  <label className="block text-sm font-semibold text-white mb-3 flex items-center space-x-2">
                    <Zap className="w-4 h-4 text-cpx-green" />
                    <span>Market Question *</span>
                  </label>
                  <input
                    {...register('question', { 
                      required: 'Question is required',
                      minLength: { value: 10, message: 'Question must be at least 10 characters' },
                      maxLength: { value: 200, message: 'Question must be less than 200 characters' }
                    })}
                    className={`input ${errors.question ? 'border-red-300' : ''}`}
                    placeholder="Will Bitcoin exceed $130,000 by December 31, 2025? (Press Enter to start AI conversation)"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && watchedValues.question && watchedValues.question.length >= 10) {
                        e.preventDefault();
                        // Start AI conversation on Enter
                        autoAnalyzeQuestion();
                        setConversation(true);
                      }
                    }}
                  />
                  {errors.question && (
                    <p className="mt-1 text-sm text-red-600">{errors.question.message}</p>
                  )}
                  <div className="mt-3 text-xs text-gray-400 flex items-center space-x-2">
                    <Brain className="w-3 h-3 text-cpx-green" />
                    <span>Press Enter to start AI-powered validation</span>
                  </div>
                </div>
                )}


                {/* AI CONVERSATION OR NEWS BOX */}
              {validationResult?.approved ? (
                <div className="card p-4 bg-green-50 border-green-200">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-green-800 font-medium">✅ AI Approved Your Market</span>
                  </div>
                </div>
              ) : creationMode === 'news' ? (
                <div className="p-6 space-y-6">
                  {!newsProgress && (
                    <>
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-white mb-2">📰 Auto-Create from News Article</h3>
                        <p className="text-gray-400">AI will find an article and create a market automatically</p>
                      </div>
                      
                      <button
                        onClick={async () => {
                          if (isValidating) return;
                          setIsValidating(true);
                          setNewsProgress({ step: 1, stepName: 'Starting...', data: {} });
                          
                          try {
                            // Start job - returns immediately
                            const startResponse = await axios.post(`${API_BASE_URL}/api/markets/predict-the-news/start`);
                            const jobId = startResponse.data.jobId;
                            
                            // Poll for updates
                            const interval = setInterval(async () => {
                              try {
                                const statusResponse = await axios.get(`${API_BASE_URL}/api/markets/predict-the-news/status/${jobId}`);
                                const job = statusResponse.data;
                                
                                // LOG EACH STEP SO USER CAN SEE PROGRESS
                                console.log(`📊 News Market Progress: Step ${job.currentStep}/6 - ${job.stepName} (${job.status})`);
                                
                                setNewsProgress({
                                  step: job.currentStep,
                                  stepName: job.stepName,
                                  status: job.status,
                                  data: job.data || {}
                                });
                                
                                if (job.status === 'complete') {
                                  clearInterval(interval);
                                  console.log('✅ News market creation COMPLETE! Navigating to market page...');
                                  setTimeout(() => navigate(`/markets/${job.data?.marketId}`), 3000);
                                } else if (job.status === 'failed') {
                                  console.error('❌ News market creation FAILED:', job.data?.error);
                                  clearInterval(interval);
                                  setIsValidating(false);
                                }
                              } catch (pollError) {
                                console.error('Poll error:', pollError);
                              }
                            }, 1000);
                            
                            // Cleanup after 5 minutes (blockchain can take time)
                            setTimeout(() => {
                              clearInterval(interval);
                              if (newsProgress?.status !== 'complete') {
                                setNewsProgress({ 
                                  step: 'failed', 
                                  error: 'Timeout after 5 minutes - market may have been created, check Markets page', 
                                  data: newsProgress?.data || {} 
                                });
                                setIsValidating(false);
                              }
                            }, 300000);
                            
                          } catch (error: any) {
                            setNewsProgress({ step: 'failed', error: error.message || 'Failed', data: {} });
                            setIsValidating(false);
                          }
                        }}
                        disabled={isValidating}
                        className="w-full btn-primary py-4 text-lg"
                      >
                        {isValidating ? '⏳ Creating...' : '🚀 Start Auto-Creation'}
                      </button>
                    </>
                  )}
                  
                  {newsProgress && newsProgress.status !== 'complete' && newsProgress.status !== 'failed' && (
                    <div className="space-y-6">
                      {/* Current Step - ALWAYS SHOW ALL 6 STEPS */}
                      <div className="text-center">
                        <Loader className="w-12 h-12 animate-spin text-primary-500 mx-auto mb-4" />
                        <p className="text-white font-bold text-xl">
                          {newsProgress.stepName || `Step ${newsProgress.step || 1}/6: Processing...`}
                        </p>
                        <p className="text-gray-400 text-sm mt-2">Step {newsProgress.step || 1} of 6</p>
                        
                        {/* Progress bar for ALL 6 steps */}
                        <div className="mt-4 max-w-md mx-auto">
                          <div className="flex justify-between text-xs text-gray-500 mb-2">
                            <span className={newsProgress.step >= 1 ? 'text-primary-400 font-bold' : ''}>Article</span>
                            <span className={newsProgress.step >= 2 ? 'text-primary-400 font-bold' : ''}>Question</span>
                            <span className={newsProgress.step >= 3 ? 'text-primary-400 font-bold' : ''}>Validate</span>
                            <span className={newsProgress.step >= 4 ? 'text-primary-400 font-bold' : ''}>Odds</span>
                            <span className={newsProgress.step >= 5 ? 'text-primary-400 font-bold' : ''}>Blockchain</span>
                            <span className={newsProgress.step >= 6 ? 'text-primary-400 font-bold' : ''}>Database</span>
                          </div>
                          <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-primary-500 to-green-500 transition-all duration-500"
                              style={{ width: `${((newsProgress.step || 1) / 6) * 100}%` }}
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Show article if we have it - KEEP VISIBLE */}
                      {newsProgress.data?.article && (
                        <div className="bg-gray-800/50 p-6 rounded-lg border border-primary-500/50">
                          <p className="text-sm text-primary-400 mb-3">📰 Article Found:</p>
                          <h4 className="text-white font-bold text-lg mb-2">{newsProgress.data.article.title || 'Sports News'}</h4>
                          {newsProgress.data.article.description && (
                            <p className="text-gray-300 text-sm mb-3 line-clamp-4">{newsProgress.data.article.description.substring(0, 300)}...</p>
                          )}
                          {newsProgress.data.article.url && newsProgress.data.article.url !== 'https://espn.com' && (
                            <a href={newsProgress.data.article.url} target="_blank" rel="noopener noreferrer" className="text-primary-500 text-sm hover:underline">
                              Read Full Article →
                            </a>
                          )}
                        </div>
                      )}
                      
                      {/* Show question if we have it */}
                      {newsProgress.data?.question && (
                        <div className="bg-primary-900/30 p-6 rounded-lg border border-primary-500">
                          <p className="text-sm text-primary-400 mb-2">🎯 Market Question:</p>
                          <p className="text-white font-bold text-xl">{newsProgress.data.question}</p>
                        </div>
                      )}
                      
                      {/* Show validation if we have it */}
                      {newsProgress.data?.validationPlan && (
                        <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
                          <p className="text-sm text-gray-400 mb-2">✅ Validation:</p>
                          <p className="text-white text-sm">Confidence: {newsProgress.data.validationPlan.confidence || 90}%</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {newsProgress && newsProgress.status === 'complete' && (
                    <div className="space-y-6">
                      <div className="text-center">
                        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                        <p className="text-white font-bold text-2xl mb-2">✅ Market Created Successfully!</p>
                      </div>
                      
                      {/* Show article */}
                      {newsProgress.data?.article && (
                        <div className="bg-gray-800/50 p-6 rounded-lg border border-primary-500/50">
                          <p className="text-sm text-primary-400 mb-3 font-bold">📰 Source Article:</p>
                          <h4 className="text-white font-bold text-lg mb-3">{newsProgress.data.article.title || 'Sports News'}</h4>
                          {newsProgress.data.article.description && (
                            <p className="text-gray-300 text-sm mb-3">{newsProgress.data.article.description.substring(0, 400)}...</p>
                          )}
                          {newsProgress.data.article.url && newsProgress.data.article.url !== 'https://espn.com' && (
                            <a href={newsProgress.data.article.url} target="_blank" rel="noopener noreferrer" 
                               className="text-primary-500 text-sm hover:underline font-semibold">
                              Read Full Article →
                            </a>
                          )}
                        </div>
                      )}
                      
                      {/* Show market question */}
                      {newsProgress.data?.question && (
                        <div className="bg-primary-900/30 p-6 rounded-lg border-2 border-primary-500">
                          <p className="text-sm text-primary-400 mb-3 font-bold">🎯 Market Question:</p>
                          <p className="text-white font-bold text-xl">{newsProgress.data.question}</p>
                        </div>
                      )}
                      
                      <button 
                        onClick={() => navigate(`/markets/${newsProgress.data?.marketId}`)}
                        className="w-full btn-primary py-4 text-lg font-bold"
                      >
                        View Market & Start Betting →
                      </button>
                    </div>
                  )}
                  
                  {newsProgress && newsProgress.step === 'failed' && (
                    <div className="space-y-4">
                      <div className="text-center">
                        <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                        <p className="text-white font-bold text-xl">Failed to Create Market</p>
                        <p className="text-red-400 mt-2">{newsProgress.error}</p>
                      </div>
                      <button 
                        onClick={() => { setNewsProgress(null); setIsValidating(false); }} 
                        className="w-full btn-primary py-4"
                      >
                        Try Again
                      </button>
                    </div>
                  )}
                </div>
              ) : conversation ? (
                <ConversationalValidation
                  question={watchedValues.question}
                  category={watchedValues.category || 'other'}
                  onApproved={(analysisData) => {
                    console.log('📅 CRITICAL: AI APPROVED WITH DATA:', analysisData);
                    
                    // Use the cleaned up question from AI if available
                    const cleanedQuestion = analysisData.clean_question || analysisData.cleanQuestion;
                    if (cleanedQuestion) {
                      console.log('✨ Using AI-cleaned question:', cleanedQuestion);
                      setValue('question', cleanedQuestion);
                    }
                    
                    // CRITICAL: USE BACKEND-PROVIDED DATES IF AVAILABLE (check both locations)
                    const bettingEndsAt = analysisData.bettingEndsAt || analysisData.timing?.bettingEndsAt;
                    const resolutionTime = analysisData.resolutionTime || analysisData.timing?.resolutionTime;
                    
                    if (bettingEndsAt && resolutionTime) {
                      console.log('🚀 USING BACKEND-PROVIDED DATES!');
                      console.log('📅 Betting ends at:', bettingEndsAt);
                      console.log('📅 Resolution time:', resolutionTime);
                      console.log('📋 Data source:', analysisData.bettingEndsAt ? 'direct' : 'timing object');
                      setValue('bettingEndsAt', bettingEndsAt);
                      setValue('resolutionTime', resolutionTime);
                      
                      // Set validation result and PROCEED IMMEDIATELY
                      console.log('🎲 AI ODDS FROM BACKEND:', analysisData.aiOdds);
                      setValidationResult({
                        approved: true,
                        approvalCount: 1,
                        totalValidators: 1,
                        reason: 'AI approved after conversation',
                        // Add the properties the UI expects
                        clean_question: analysisData.clean_question || analysisData.cleanQuestion,
                        interpretation: analysisData.interpretation,
                        specialist: analysisData.specialist, // CRITICAL: Pass specialist field for category detection!
                        isDemo: analysisData.isDemo,
                        aiOdds: analysisData.aiOdds, // 🎲 CRITICAL: Include AI odds!
                        timing: analysisData.timing || {
                          bettingEndsAt: bettingEndsAt,
                          resolutionTime: resolutionTime
                        },
                        validations: [{
                          validatorId: 'CONVERSATIONAL_AI',
                          decision: 'APPROVE',
                          reasoning: analysisData.interpretation || 'Approved',
                          confidence: analysisData.confidence || 90,
                          dataSources: analysisData.dataSources || [],
                          verificationMethod: analysisData.verificationMethod || '',
                          resolutionPlan: analysisData.resolutionPlan || '',
                          validationPlan: analysisData.validationPlan || null,
                          specialist: analysisData.specialist // Also in validation for backup
                        }]
                      });
                      
                      // AUTO-PROCEED TO STEP 2 IMMEDIATELY
                      toast.success('🤖 AI approved your market with dates!');
                      setStep(2);
                      return; // Exit early, dates are set!
                    }
                    
                    // FALLBACK: Extract date from AI's interpretation or clean question
                    const textToSearch = analysisData.cleanQuestion || analysisData.interpretation || '';
                    console.log('⚠️ No backend dates, extracting from text:', textToSearch);
                    console.log('🔍 Clean question:', analysisData.cleanQuestion);
                    console.log('🔍 Interpretation:', analysisData.interpretation);
                    
                    // Find ALL dates first
                    const allDates = [];
                    
                    // Format: Month DD, YYYY or Month D, YYYY (including time like "September 23, 2025 at 10:10 PM")
                    const monthNameRegex = /(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{1,2}),?\s+(\d{4})(?:\s+at\s+[\d:]+\s*[AP]M)?/gi;
                    let match;
                    while ((match = monthNameRegex.exec(textToSearch)) !== null) {
                      const [, month, day, year] = match;
                      const monthNum = {
                        'january': '01', 'february': '02', 'march': '03', 'april': '04',
                        'may': '05', 'june': '06', 'july': '07', 'august': '08',
                        'september': '09', 'october': '10', 'november': '11', 'december': '12'
                      }[month.toLowerCase()];
                      const dateStr = `${year}-${monthNum}-${day.padStart(2, '0')}`;
                      // CRITICAL: Parse date in LOCAL timezone, not UTC!
                      // Using "YYYY-MM-DD" format causes UTC parsing, which shifts the date
                      const dateObj = new Date(parseInt(year), parseInt(monthNum) - 1, parseInt(day));
                      console.log(`📅 Found date via month name: ${month} ${day}, ${year} -> ${dateStr} (${dateObj.toDateString()})`);
                      allDates.push({ 
                        date: dateStr, 
                        timestamp: dateObj.getTime(),
                        source: `${month} ${day}, ${year}`
                      });
                    }
                    
                    // Format: MM/DD/YYYY
                    const slashDateRegex = /(\d{1,2})\/(\d{1,2})\/(\d{4})/g;
                    while ((match = slashDateRegex.exec(textToSearch)) !== null) {
                      const [, month, day, year] = match;
                      const dateStr = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                      const dateObj = new Date(dateStr);
                      console.log(`📅 Found date via slash format: ${month}/${day}/${year} -> ${dateStr}`);
                      allDates.push({ 
                        date: dateStr, 
                        timestamp: dateObj.getTime(),
                        source: `${month}/${day}/${year}`
                      });
                    }
                    
                    // Format: YYYY-MM-DD
                    const isoDateRegex = /(\d{4})-(\d{2})-(\d{2})/g;
                    while ((match = isoDateRegex.exec(textToSearch)) !== null) {
                      const dateStr = match[0];
                      const dateObj = new Date(dateStr);
                      console.log(`📅 Found date via ISO format: ${dateStr}`);
                      allDates.push({ 
                        date: dateStr, 
                        timestamp: dateObj.getTime(),
                        source: dateStr
                      });
                    }
                    
                    // Special cases if no dates found
                    if (allDates.length === 0) {
                      if (textToSearch.toLowerCase().includes('tonight') || textToSearch.toLowerCase().includes('today')) {
                      const today = new Date().toISOString().split('T')[0];
                        allDates.push({ 
                          date: today, 
                          timestamp: new Date(today).getTime(),
                          source: 'today'
                        });
                      } else if (textToSearch.toLowerCase().includes('tomorrow')) {
                        const tomorrow = new Date();
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        const tomorrowStr = tomorrow.toISOString().split('T')[0];
                        allDates.push({ 
                          date: tomorrowStr, 
                          timestamp: tomorrow.getTime(),
                          source: 'tomorrow'
                        });
                      }
                    }
                    
                    // CRITICAL: Use the dates from the backend, don't parse them again!
                    if (analysisData.bettingEndsAt && analysisData.resolutionTime) {
                      console.log('📅 Using backend-validated dates:');
                      console.log('  Betting ends:', analysisData.bettingEndsAt);
                      console.log('  Resolution:', analysisData.resolutionTime);
                      
                      setValue('bettingEndsAt', analysisData.bettingEndsAt);
                      setValue('resolutionTime', analysisData.resolutionTime);
                      
                      // Verify they're in the future for blockchain
                      const deadlineUnix = Math.floor(new Date(analysisData.resolutionTime).getTime() / 1000);
                      const nowUnix = Math.floor(Date.now() / 1000);
                      console.log('📅 Deadline Unix:', deadlineUnix, 'Now Unix:', nowUnix);
                      console.log('📅 Is future?', deadlineUnix > nowUnix ? '✅ YES' : '❌ NO - BLOCKCHAIN WILL REJECT');
                      
                      // Skip all the date parsing below
                      return;
                    }
                    
                    console.warn('⚠️ Backend did not provide dates, falling back to parsing');
                    // CRITICAL: For markets with date RANGES, we need the END date as deadline!
                    let foundDate = null;
                    if (allDates.length > 0) {
                      console.log('📅 All dates found:', allDates.map(d => `${d.source} -> ${d.date} (${new Date(d.timestamp).toLocaleDateString()})`));
                      
                      // Sort by timestamp to get them in order
                      allDates.sort((a, b) => a.timestamp - b.timestamp);
                      
                      // ALWAYS use the LAST date as the market deadline
                      // For "between X and Y" questions, Y is the deadline
                      foundDate = allDates[allDates.length - 1].date;
                      
                      if (allDates.length === 2) {
                        console.log(`📅 Found date RANGE: ${allDates[0].date} to ${allDates[1].date}`);
                        console.log(`📅 Using END date as deadline: ${foundDate}`);
                      } else if (allDates.length > 2) {
                        console.log(`📅 Found ${allDates.length} dates, using LATEST as deadline: ${foundDate}`);
                      } else {
                        console.log(`📅 Found single date as deadline: ${foundDate}`);
                      }
                      
                      // Verify the deadline is in the future
                      const deadlineTimestamp = new Date(foundDate).getTime();
                      const nowTimestamp = Date.now();
                      
                      if (deadlineTimestamp <= nowTimestamp) {
                        console.error('❌ DEADLINE IS IN THE PAST! Blockchain will reject!');
                        console.error(`❌ Deadline: ${foundDate} (${new Date(deadlineTimestamp).toLocaleString()})`);
                        console.error(`❌ Current: ${new Date(nowTimestamp).toLocaleString()}`);
                        // Try to salvage by adding time if it's today
                        if (foundDate === new Date().toISOString().split('T')[0]) {
                          // If deadline is today, set to end of day
                          foundDate = new Date().toISOString().split('T')[0] + 'T23:59:59';
                          console.log('📅 Adjusted to end of today:', foundDate);
                        }
                      }
                    }
                    
                    if (foundDate) {
                      // CRITICAL: Parse the date correctly handling timezone issues
                      // The AI says "September 23, 2025 at 10:30 PM EST"
                      // We need to extract the time from the original question
                      let deadlineDate;
                      
                      // Check if the original question has a specific time
                      const timeMatch = analysisData.cleanQuestion?.match(/at\s+([\d:]+)\s*([ap]\.?m\.?)/i) || 
                                       analysisData.interpretation?.match(/at\s+([\d:]+)\s*([ap]\.?m\.?)/i);
                      
                      if (timeMatch) {
                        const [, time, meridiem] = timeMatch;
                        const timeParts = time.split(':');
                        const hours = parseInt(timeParts[0]);
                        const minutes = timeParts[1] ? parseInt(timeParts[1]) : 30; // Default to :30 if no minutes
                        let adjustedHours = hours;
                        
                        if (meridiem.toLowerCase().includes('p') && hours !== 12) {
                          adjustedHours += 12;
                        } else if (meridiem.toLowerCase().includes('a') && hours === 12) {
                          adjustedHours = 0;
                        }
                        
                        // CRITICAL FIX: Use the date components to create a LOCAL date with the specified time
                        const [year, month, day] = foundDate.split('-').map(n => parseInt(n));
                        
                        // Create the date in local timezone with the exact time specified
                        deadlineDate = new Date(year, month - 1, day, adjustedHours, minutes, 0, 0);
                        
                        console.log(`📅 Extracted time from question: ${time} ${meridiem} -> ${adjustedHours}:${minutes.toString().padStart(2, '0')}`);
                        console.log(`📅 Creating deadline: ${month}/${day}/${year} at ${adjustedHours}:${minutes.toString().padStart(2, '0')}`);
                      } else {
                        // No time specified, use end of day
                        const [year, month, day] = foundDate.split('-').map(n => parseInt(n));
                        deadlineDate = new Date(year, month - 1, day, 23, 59, 59, 999);
                      }
                      
                      // FINAL CHECK: Make sure the deadline is actually in the future
                      const now = new Date();
                      const currentUnix = Math.floor(now.getTime() / 1000);
                      const deadlineUnix = Math.floor(deadlineDate.getTime() / 1000);
                      
                      console.log('📅 Original date string:', foundDate);
                      console.log('📅 Parsed as local date:', deadlineDate.toString());
                      console.log('📅 Deadline Unix:', deadlineUnix, '- Current Unix:', currentUnix);
                      console.log('📅 Time difference (seconds):', deadlineUnix - currentUnix);
                      
                      // If the deadline is somehow in the past, adjust it
                      if (deadlineUnix <= currentUnix) {
                        console.warn('⚠️ Deadline was in the past, adjusting...');
                        // If we're on the same day, add 24 hours
                        if (deadlineDate.toDateString() === now.toDateString()) {
                          deadlineDate.setDate(deadlineDate.getDate() + 1);
                          console.log('📅 Added 24 hours, new deadline:', deadlineDate.toString());
                        }
                      }
                      
                      const isoString = deadlineDate.toISOString();
                      
                      console.log('📅 Final ISO string:', isoString);
                      console.log('📅 Final Unix timestamp:', Math.floor(deadlineDate.getTime() / 1000));
                      console.log('📅 Is future?', deadlineDate.getTime() > Date.now() ? '✅ YES' : '❌ NO');
                      
                      setValue('bettingEndsAt', isoString);
                      setValue('resolutionTime', isoString);
                      console.log('📅 Set market deadline to:', isoString);
                    } else {
                      console.warn('⚠️ Could not extract date from AI response');
                      // Try to find ANY date mention in the text
                      const yearMatch = textToSearch.match(/\b(2025|2026|2027)\b/);
                      if (yearMatch) {
                        // Default to end of current month if we found a year
                        const defaultDate = new Date();
                        defaultDate.setMonth(defaultDate.getMonth() + 1, 0); // Last day of current month
                        const fallbackDate = defaultDate.toISOString().split('T')[0];
                        console.log('📅 Using fallback date (end of month):', fallbackDate);
                        setValue('bettingEndsAt', fallbackDate);
                        setValue('resolutionTime', fallbackDate);
                      }
                    }
                    
                    // Save validation result
                    console.log('🎲🎲🎲 AI ODDS FROM BACKEND (SECOND CALL):', analysisData.aiOdds);
                    setValidationResult({
                      approved: true,
                      approvalCount: 1,
                      totalValidators: 1,
                      reason: 'AI approved after conversation',
                      // Add the properties the UI expects
                      clean_question: analysisData.clean_question || analysisData.cleanQuestion,
                      interpretation: analysisData.interpretation,
                      specialist: analysisData.specialist, // CRITICAL: Pass specialist for category/widget detection!
                      isDemo: analysisData.isDemo,
                      aiOdds: analysisData.aiOdds, // 🎲 CRITICAL: Include AI odds HERE TOO!
                      timing: analysisData.timing || {
                        bettingEndsAt: analysisData.bettingEndsAt,
                        resolutionTime: analysisData.resolutionTime
                      },
                      validations: [{
                        validatorId: 'CONVERSATIONAL_AI',
                        decision: 'APPROVE',
                        reasoning: analysisData.interpretation || 'Approved',
                        confidence: analysisData.confidence || 90,
                        dataSources: analysisData.dataSources || [],
                        verificationMethod: analysisData.verificationMethod || '',
                        resolutionPlan: analysisData.resolutionPlan || '',
                        validationPlan: analysisData.validationPlan || null,  // PASS THE VALIDATION PLAN
                        specialist: analysisData.specialist // Also in validation for backup
                      }]
                    })
                    // Auto-proceed to Step 2
                    toast.success('🤖 AI approved your market!')
                        setStep(2)
                  }}
                  onCancel={() => {
                    setValidationResult(null)
                    setConversation(false)
                    // Stay in Step 1
                  }}
                />
              ) : watchedValues.question && watchedValues.question.length >= 10 ? (
                <div className="card p-8 text-center bg-gradient-to-br from-[#052393]/20 to-[#2AAAEA]/10 border-[#2AAAEA]/40">
                  <img 
                    src="/cpx-logo.jpg" 
                    alt="CPX Logo" 
                    className="w-16 h-16 rounded-xl mx-auto mb-4 shadow-lg shadow-[#2AAAEA]/30"
                  />
                  <h3 className="text-2xl font-black text-white mb-2">Ready for AI Analysis</h3>
                  <p className="text-gray-300 mb-6">Chat with AI to design your pool</p>
                  <button
                  onClick={() => {
                    console.log('🔵 Button clicked - starting conversation');
                    autoAnalyzeQuestion();
                    setConversation(true);
                  }}
                    className="bg-gradient-to-r from-[#2AAAEA] to-[#052393] hover:shadow-lg hover:shadow-[#2AAAEA]/50 text-white font-bold px-8 py-3 rounded-xl flex items-center space-x-2 mx-auto transition-all transform hover:scale-105"
                  >
                      <Zap className="w-5 h-5" />
                    <span>Start AI Conversation</span>
                </button>
              </div>
              ) : null}

                {/* Fallback hint if no question entered yet - ONLY in manual mode */}
                {creationMode === 'manual' && (!watchedValues.question || watchedValues.question.length < 10) && (
                  <div className="text-center py-8">
                    <img 
                      src="/cpx-logo.jpg" 
                      alt="CPX Logo" 
                      className="w-12 h-12 rounded-lg mx-auto mb-4 opacity-50"
                    />
                    <p className="text-gray-400 text-sm">Enter a pool question above to start AI conversation</p>
                  </div>
                )}
              </div>
            </div>

            {/* Creator Rules - Full width with examples button */}
            <div className="card p-6 bg-[#052393]/10 border-[#2AAAEA]/30">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-white text-lg">📋 Pool Creator Rules</h3>
                <button
                  type="button"
                  onClick={() => setShowExamplesModal(true)}
                  className="text-sm font-bold text-cpx-light-blue hover:text-glow-aqua flex items-center space-x-2 transition-colors"
                >
                  <Info className="w-4 h-4" />
                  <span>See Good Examples</span>
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-white">
                <div className="flex items-center space-x-3 bg-[#001233] p-3 rounded-lg border border-[#052393]/40">
                  <span className="w-6 h-6 bg-gradient-to-br from-[#2AAAEA] to-[#052393] text-white rounded-full flex items-center justify-center text-xs font-black flex-shrink-0">1</span>
                  <span><strong className="text-cpx-light-blue">YES OR NO</strong> questions only</span>
                </div>
                <div className="flex items-center space-x-3 bg-[#001233] p-3 rounded-lg border border-[#052393]/40">
                  <span className="w-6 h-6 bg-gradient-to-br from-[#2AAAEA] to-[#052393] text-white rounded-full flex items-center justify-center text-xs font-black flex-shrink-0">2</span>
                  <span><strong className="text-cpx-light-blue">Publicly Verifiable</strong> (or rejected)</span>
                </div>
                <div className="flex items-center space-x-3 bg-[#001233] p-3 rounded-lg border border-[#052393]/40">
                  <span className="w-6 h-6 bg-gradient-to-br from-[#2AAAEA] to-[#052393] text-white rounded-full flex items-center justify-center text-xs font-black flex-shrink-0">3</span>
                  <span><strong className="text-cpx-light-blue">END Date Required</strong> (Further out = more bets)</span>
                </div>
                <div className="flex items-center space-x-3 bg-[#001233] p-3 rounded-lg border border-[#052393]/40">
                  <span className="w-6 h-6 bg-gradient-to-br from-[#2AAAEA] to-[#052393] text-white rounded-full flex items-center justify-center text-xs font-black flex-shrink-0">4</span>
                  <span><strong className="text-cpx-light-blue">Controversy = Rewards</strong> (No losing side = no fees)</span>
                </div>
              </div>
            </div>

            {/* INSPIRATION TEMPLATES - SEPARATE CARD */}
            <div className="card p-8">
              <label className="block text-sm font-semibold text-white mb-3">
                Need Ideas? <span className="text-gray-200 font-normal">Browse popular market types</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                {marketTemplates.filter(t => t.id !== 'custom_market').map(template => (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => applyTemplate(template.id)}
                    className={`p-3 text-left border rounded-lg transition-all duration-200 ${
                      watchedValues.template === template.id 
                        ? 'border-[#2AAAEA] bg-[#2AAAEA]/10 shadow-lg shadow-[#2AAAEA]/30' 
                        : 'border-[#052393]/40 bg-[#001233] hover:border-[#2AAAEA]/50 hover:bg-[#052393]/20'
                    }`}
                  >
                    <div className="text-sm font-bold text-white">{template.name}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      {categories.find(c => c.id === template.category)?.icon} {categories.find(c => c.id === template.category)?.name}
                    </div>
                  </button>
                ))}
              </div>

              <div className="text-xs text-gray-300 mt-2 flex items-center space-x-2">
                <span className="text-cpx-yellow text-lg">💡</span>
                <span>These are just inspiration - click one to see example formats, then customize with your own details.</span>
              </div>
            </div>
          </div>
        )

      case 2:
        // Don't auto-set date - AI already set it correctly!
        
        return (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-4xl font-black text-white mb-2">AI <span className="text-gradient">Pool</span> Analysis</h2>
              <p className="text-gray-300">AI has analyzed your pool and confirmed the details</p>
            </div>

            {/* REAL AI Validation Results */}
            {validationResult ? (
              <div className={`card p-6 ${validationResult.approved ? 'bg-[#009245]/10 border-[#009245]/30' : 'bg-red-900/20 border-red-500/30'}`}>
                <div className="flex items-start space-x-3 mb-4">
                  <img 
                    src="/cpx-logo.jpg" 
                    alt="CPX AI" 
                    className="w-8 h-8 rounded-lg shadow-lg shadow-[#2AAAEA]/30"
                  />
                  <div className="flex-1">
                    <h3 className={`text-xl font-black mb-2 ${validationResult.approved ? 'text-cpx-green' : 'text-red-400'}`}>
                      🤖 {validationResult.approved ? 'AI Approved Your Pool!' : 'AI Rejected Your Pool'}
                    </h3>
                    <p className={`text-sm mb-4 ${validationResult.approved ? 'text-gray-300' : 'text-gray-300'}`}>
                      {validationResult.details?.validations?.[0]?.reasoning || 'AI validation complete'}
                    </p>
                    
                    {/* Show detailed AI analysis */}
                    {validationResult.details?.validations?.[0] && (
                      <div className="mt-4 space-y-3">
                        {validationResult.details.validations[0].dataSources?.length > 0 && (
                          <div className="bg-[#052393]/20 p-3 rounded-lg border border-[#2AAAEA]/30">
                            <h4 className="font-bold text-white mb-2">🔧 Data Sources:</h4>
                            <div className="text-sm text-gray-300">
                              {validationResult.details.validations[0].dataSources.join(', ')}
                            </div>
                          </div>
                        )}
                        
                        {validationResult.details.validations[0].verificationMethod && (
                          <div className="bg-[#009245]/10 p-3 rounded-lg border border-[#009245]/30">
                            <h4 className="font-bold text-white mb-2">🔍 Verification Method:</h4>
                            <div className="text-sm text-gray-300">
                              {validationResult.details.validations[0].verificationMethod}
                            </div>
                          </div>
                        )}
                        
                        {validationResult.details.validations[0].potentialIssues?.length > 0 && (
                          <div className="bg-[#FBB03B]/10 p-3 rounded-lg border border-[#FBB03B]/30">
                            <h4 className="font-bold text-cpx-yellow mb-2">⚠️ Potential Issues:</h4>
                            <ul className="text-sm text-gray-300 list-disc list-inside">
                              {validationResult.details.validations[0].potentialIssues.map((issue, i) => (
                                <li key={i}>{issue}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {validationResult.details.validations[0].resolutionPlan && (
                          <div className="bg-[#052393]/20 p-3 rounded-lg border border-[#2AAAEA]/30">
                            <h4 className="font-bold text-white mb-2">📋 Resolution Plan:</h4>
                            <div className="text-sm text-gray-300 whitespace-pre-line">
                              {validationResult.details.validations[0].resolutionPlan}
                            </div>
                          </div>
                        )}
                        
                        <div className="bg-[#001233] p-3 rounded-lg border border-[#052393]/40">
                          <h4 className="font-bold text-white mb-2">🎯 AI Confidence:</h4>
                          <div className="flex items-center space-x-2">
                            <div className="flex-1 bg-[#052393]/30 rounded-full h-2.5">
                              <div 
                                className="bg-gradient-to-r from-[#2AAAEA] to-[#009245] h-2.5 rounded-full transition-all duration-500" 
                                style={{ width: `${validationResult.details.validations[0].confidence || 0}%` }}
                              ></div>
                            </div>
                            <span className="text-sm font-black text-cpx-green">
                              {validationResult.details.validations[0].confidence || 0}%
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="card p-6 bg-[#001233] border-[#052393]/40">
                <div className="text-center">
                  <img 
                    src="/cpx-logo.jpg" 
                    alt="CPX" 
                    className="w-12 h-12 rounded-lg mx-auto mb-4 opacity-30"
                  />
                  <p className="text-gray-400">No validation results yet</p>
                </div>
              </div>
            )}

            {/* AI ODDS DISPLAY */}
            {validationResult?.aiOdds && validationResult.aiOdds.confidence > 0 && (
              <div className="card p-6 bg-[#052393]/10 border-[#2AAAEA]/30 mb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-black text-white flex items-center">
                    🎲 AI Market Odds
                    <button
                      type="button"
                      onClick={() => setShowOddsReasoningModal(true)}
                      className="ml-2 text-gray-400 hover:text-white transition-colors"
                      title="View AI reasoning"
                    >
                      <Info className="w-5 h-5" />
                    </button>
                  </h3>
                  <span className="text-xs text-gray-400">
                    AI Confidence: {validationResult.aiOdds.confidence}%
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div className="bg-green-500/10 p-4 rounded-lg border border-green-500/30 text-center">
                    <div className="text-xs text-gray-400 mb-1">YES</div>
                    <div className="text-3xl font-black text-green-400">
                      {Math.round((validationResult.aiOdds.oddsYes || 0.5) * 100)}%
                    </div>
                  </div>
                  <div className="bg-red-500/10 p-4 rounded-lg border border-red-500/30 text-center">
                    <div className="text-xs text-gray-400 mb-1">NO</div>
                    <div className="text-3xl font-black text-red-400">
                      {Math.round((validationResult.aiOdds.oddsNo || 0.5) * 100)}%
                    </div>
                  </div>
                </div>
                
                {validationResult.aiOdds.reasoning && (
                  <p className="text-xs text-gray-400 line-clamp-2">
                    {validationResult.aiOdds.reasoning.slice(0, 200)}...
                  </p>
                )}
              </div>
            )}

              {/* What AI Resolved/Confirmed */}
              <div className="bg-[#009245]/10 p-4 rounded-lg mb-4 border border-[#009245]/30">
                <h4 className="font-bold text-white mb-3 flex items-center">
                  <CheckCircle className="w-5 h-5 text-cpx-green mr-2" />
                  AI Confirmed Pool Details:
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex">
                    <span className="font-bold text-gray-400 w-28 uppercase tracking-wider text-xs">Question:</span>
                    <span className="text-white">"{watchedValues.question}"</span>
                  </div>
                  <div className="flex">
                    <span className="font-bold text-gray-400 w-28 uppercase tracking-wider text-xs">Category:</span>
                    <span className="text-white capitalize">{watchedValues.category}</span>
                  </div>
                  <div className="flex">
                    <span className="font-bold text-gray-400 w-28 uppercase tracking-wider text-xs">Data Source:</span>
                    <span className="text-white">{dataSources[watchedValues.category as keyof typeof dataSources]?.source}</span>
                  </div>
                </div>
              </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="btn-secondary flex items-center space-x-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back</span>
              </button>
              {validationResult?.approved && (
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  className="bg-gradient-to-r from-[#2AAAEA] to-[#052393] hover:shadow-lg hover:shadow-[#2AAAEA]/50 text-white font-bold px-6 py-3 rounded-xl flex items-center space-x-2 transition-all transform hover:scale-105"
                >
                  <span>Continue to Your Bet</span>
                  <Calendar className="w-4 h-4" />
                </button>
              )}
            </div>


          </div>
        )

      case 3:
        return (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-4xl font-black text-white mb-2">Place <span className="text-gradient">Your</span> Bet</h2>
              <p className="text-gray-300">You must bet on your own pool to launch it</p>
            </div>

            {/* AI Validation Preview - BEFORE betting */}
            {validationResult?.interpretation && (
              <div className="card p-6 bg-blue-950/30 border-2 border-blue-500/30">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <Info className="w-5 h-5 mr-2 text-blue-400" />
                  🎯 AI Validation Details - Review Before Betting!
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {validationResult.clean_question && (
                    <div className="bg-gray-900/50 p-3 rounded border border-gray-700">
                      <span className="font-medium text-blue-400">Clean Question:</span>
                      <p className="text-white mt-1">{validationResult.clean_question}</p>
                    </div>
                  )}
                  {validationResult.interpretation?.current_value && (
                    <div className="bg-gray-900/50 p-3 rounded border border-gray-700">
                      <span className="font-medium text-blue-400">Current Value:</span>
                      <span className="ml-2 text-white font-bold text-lg">{validationResult.interpretation.current_value}</span>
                    </div>
                  )}
                  {validationResult.interpretation?.target && (
                    <div className="bg-gray-900/50 p-3 rounded border border-gray-700">
                      <span className="font-medium text-blue-400">Target:</span>
                      <span className="ml-2 text-white font-bold text-lg">{validationResult.interpretation.target}</span>
                    </div>
                  )}
                  {validationResult.interpretation?.deadline && (
                    <div className="bg-gray-900/50 p-3 rounded border border-gray-700">
                      <span className="font-medium text-blue-400">Deadline:</span>
                      <span className="ml-2 text-white">{validationResult.interpretation.deadline}</span>
                    </div>
                  )}
                </div>
                {validationResult.interpretation?.methodology && (
                  <div className="bg-gray-900/50 p-3 rounded mt-4 border border-gray-700">
                    <span className="font-medium text-blue-400">Resolution Method:</span>
                    <p className="text-white mt-1">{validationResult.interpretation.methodology}</p>
                  </div>
                )}
                {validationResult.interpretation?.assumptions && validationResult.interpretation.assumptions.length > 0 && (
                  <div className="bg-[#1a1f2e] p-3 rounded mt-4">
                    <span className="font-medium text-blue-700">AI Will Check:</span>
                    <ul className="list-disc list-inside text-white mt-2 space-y-1">
                      {validationResult.interpretation.assumptions.map((assumption: string, i: number) => (
                        <li key={i} className="text-sm">{assumption}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="mt-4 p-3 bg-yellow-100 border border-yellow-300 rounded">
                  <p className="text-sm text-yellow-800">
                    <strong>⚠️ IMPORTANT:</strong> This market will resolve EXACTLY as described above. 
                    Review these details carefully - they determine if you win or lose!
                  </p>
                </div>
              </div>
            )}

            {/* AI Market Odds - Two Section Display */}
            {validationResult?.aiOdds && (
              <div className="space-y-4">
                {/* TOP: AI Odds Analysis (Read-Only) */}
                <div className="card p-6 bg-gradient-to-br from-[#052393]/20 to-[#2AAAEA]/10 border-2 border-[#2AAAEA]/40">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Brain className="w-6 h-6 text-[#2AAAEA]" />
                      <h3 className="text-xl font-bold text-white">🎯 AI Market Odds</h3>
                    </div>
                    <span className="text-sm text-gray-400">AI Confidence: {validationResult.aiOdds.confidence}%</span>
                  </div>

                  {/* AI Reasoning Preview */}
                  {validationResult.aiOdds.reasoning && (
                    <div className="mb-4 p-4 bg-black/20 rounded-lg">
                      <p className="text-sm text-gray-300">
                        {validationResult.aiOdds.reasoning.slice(0, 200)}...
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowOddsReasoningModal(true)}
                        className="text-sm text-[#2AAAEA] hover:text-white transition-colors mt-2 flex items-center gap-1"
                      >
                        View Full Analysis →
                      </button>
                    </div>
                  )}

                  {/* AI Recommendation Display */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-green-500/20 p-6 rounded-xl border-2 border-green-500/40 text-center">
                      <div className="text-sm text-green-300 mb-2">YES</div>
                      <div className="text-5xl font-black text-green-400">
                        {Math.round((validationResult.aiOdds.oddsYes || 0.5) * 100)}%
                      </div>
                    </div>
                    <div className="bg-red-500/20 p-6 rounded-xl border-2 border-red-500/40 text-center">
                      <div className="text-sm text-red-300 mb-2">NO</div>
                      <div className="text-5xl font-black text-red-400">
                        {Math.round((validationResult.aiOdds.oddsNo || 0.5) * 100)}%
                      </div>
                    </div>
                  </div>

                </div>

                {/* BOTTOM: Adjust Starting Odds (Creator Can Modify) */}
                <div className="card p-6 bg-[#1a1f2e] border border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                        <Target className="w-5 h-5 text-yellow-400" />
                        Customize Starting Odds <span className="text-gray-500 text-sm">(Optional)</span>
                      </h3>
                      <p className="text-xs text-gray-400 mt-1">
                        Adjust the AI recommendation to set your market's starting odds
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setCustomOddsYes(Math.round((validationResult.aiOdds.oddsYes || 0.5) * 100));
                        setUseCustomOdds(false);
                      }}
                      className="px-3 py-1.5 rounded-lg text-sm bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors"
                    >
                      Reset to AI
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">YES Percentage</span>
                      <span className="text-lg font-black text-green-400">{customOddsYes}%</span>
                    </div>
                    
                    <input
                      type="range"
                      min="5"
                      max="95"
                      value={customOddsYes}
                      onChange={(e) => {
                        setCustomOddsYes(parseInt(e.target.value));
                        setUseCustomOdds(true);
                      }}
                      className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                      style={{
                        background: `linear-gradient(to right, #10b981 0%, #10b981 ${customOddsYes}%, #374151 ${customOddsYes}%, #374151 100%)`
                      }}
                    />
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-green-500/10 p-4 rounded-lg border border-green-500/30 text-center">
                        <div className="text-3xl font-black text-green-400">{customOddsYes}%</div>
                        <div className="text-xs text-gray-400 mt-1">YES</div>
                      </div>
                      <div className="bg-red-500/10 p-4 rounded-lg border border-red-500/30 text-center">
                        <div className="text-3xl font-black text-red-400">{100 - customOddsYes}%</div>
                        <div className="text-xs text-gray-400 mt-1">NO</div>
                      </div>
                    </div>

                    {useCustomOdds && (
                      <div className="flex items-center justify-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                        <AlertTriangle className="w-4 h-4 text-yellow-500" />
                        <span className="text-sm text-yellow-500 font-bold">⚠️ Modified from AI</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Creator Bet */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="card p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <DollarSign className="w-5 h-5 mr-2 text-primary-600" />
                  Your Bet
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Bet Amount (Your Custom Tokens) *
                    </label>
                    <input
                      {...register('creationStake', { 
                        required: 'Bet amount is required',
                        min: { value: 1, message: 'Minimum bet is 1 token' },
                        max: { value: user?.tokenBalance || 10000, message: 'Insufficient balance' }
                      })}
                      type="number"
                      className={`input ${errors.creationStake ? 'border-red-300' : ''}`}
                      min="100"
                      step="100"
                      placeholder="1000"
                    />
                    {errors.creationStake && (
                      <p className="mt-1 text-sm text-red-600">{errors.creationStake.message}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Your Prediction * (Required to create pool)
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          console.log('Setting outcome to YES (1)');
                          setValue('outcome', 1, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                        }}
                        className={`p-3 rounded-lg text-center font-semibold border-2 transition-all ${
                          watchedValues.outcome === 1 
                            ? 'border-green-500 bg-green-50 text-green-700' 
                            : 'border-gray-700 hover:border-green-300 text-gray-300'
                        }`}
                      >
                        YES {watchedValues.outcome === 1 && '✓'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          console.log('Setting outcome to NO (0)');
                          setValue('outcome', 0, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                        }}
                        className={`p-3 rounded-lg text-center font-semibold border-2 transition-all ${
                          watchedValues.outcome === 0 
                            ? 'border-red-500 bg-red-50 text-red-700' 
                            : 'border-gray-700 hover:border-red-300 text-gray-300'
                        }`}
                      >
                        NO {watchedValues.outcome === 0 && '✓'}
                      </button>
                    </div>
                    {(watchedValues.outcome !== 0 && watchedValues.outcome !== 1) && (
                      <p className="mt-1 text-sm text-red-600">You must bet on your own pool to create it</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="card p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <Target className="w-5 h-5 mr-2 text-primary-600" />
                  Creator Rewards
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span>Base fee:</span>
                    <span className="font-semibold">0.25% of losing bets</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Volume bonus (25K+ total):</span>
                    <span className="font-semibold text-green-600">+0.25% of losing bets</span>
                  </div>
                  <div className="border-t pt-3">
                    <div className="flex justify-between font-semibold">
                      <span>Maximum total:</span>
                      <span className="text-green-600">0.5% of losing bets</span>
                    </div>
                  </div>
                  <div className="text-xs text-gray-200 mt-3 space-y-1">
                    <p>💡 All rewards come from the <strong>losing side only</strong></p>
                    <p>⚠️ No losers = market cancelled = no rewards</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="btn-secondary flex items-center space-x-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back</span>
              </button>
              <button
                type="button"
                onClick={() => setStep(4)}
                className="btn-primary flex items-center space-x-2"
                disabled={!watchedValues.creationStake || (watchedValues.outcome !== 0 && watchedValues.outcome !== 1)}
              >
                <span>Create Pool</span>
                <CheckCircle className="w-4 h-4" />
              </button>
            </div>
          </div>
        )

      case 4:
        return (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-4xl font-black text-white mb-2">Launch <span className="text-gradient">Your</span> Pool</h2>
              <p className="text-gray-300">Final review before your pool goes live</p>
            </div>

            <MarketPreview formData={watchedValues} />

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setStep(3)}
                className="btn-secondary flex items-center space-x-2"
                disabled={isValidating}
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back</span>
              </button>
              <button
                type="submit"
                className="bg-gradient-to-r from-[#009245] to-emerald-600 hover:shadow-lg hover:shadow-[#009245]/50 text-white font-black px-8 py-4 rounded-xl flex items-center space-x-2 transition-all transform hover:scale-105 text-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                disabled={isValidating || watchedValues.outcome === undefined}
              >
                {isValidating ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>Creating Pool...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span>LAUNCH YOUR POOL</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="min-h-screen py-12 relative overflow-hidden">
      {/* Background decorative blobs - CPX style */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-40 right-20 w-96 h-96 bg-[#2AAAEA]/10 rounded-full blur-3xl animate-pulse-soft" />
        <div className="absolute bottom-40 left-20 w-80 h-80 bg-[#009245]/10 rounded-full blur-3xl animate-pulse-soft" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-[#FBB03B]/10 rounded-full blur-3xl animate-pulse-soft" style={{ animationDelay: '2s' }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl md:text-6xl font-black text-white mb-4">
              Create <span className="text-gradient">YOUR</span> Pool
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-8">
              Chat with AI to design a prediction pool on live or upcoming events. YOU make the pool.
            </p>
            
            {/* Toggle: Make Your Own vs Get News */}
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setCreationMode('manual')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                  creationMode === 'manual'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                ⚡ Make Your Own
              </button>
              <button
                onClick={() => setCreationMode('news')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                  creationMode === 'news'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                📰 Get News Prediction
              </button>
            </div>
          </motion.div>
        </div>

        {/* Progress indicator - CPX styled */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            {[1, 2, 3, 4].map((stepNum) => (
              <div key={stepNum} className="flex items-center">
                <div className={`
                  w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold shadow-lg transition-all duration-300
                  ${step >= stepNum 
                    ? 'bg-gradient-to-br from-[#2AAAEA] to-[#052393] text-white shadow-[#2AAAEA]/50' 
                    : 'bg-[#052393]/20 border-2 border-[#052393]/40 text-gray-500'
                  }
                `}>
                  {stepNum}
                </div>
                {stepNum < 4 && (
                  <div className={`
                    w-16 h-1 mx-2 rounded-full transition-all duration-300
                    ${step > stepNum ? 'bg-gradient-to-r from-[#2AAAEA] to-[#052393]' : 'bg-[#052393]/20'}
                  `} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs font-bold text-gray-400 uppercase tracking-wider">
            <span className={step === 1 ? 'text-cpx-light-blue' : ''}>Chat with AI</span>
            <span className={step === 2 ? 'text-cpx-light-blue' : ''}>Verify Method</span>
            <span className={step === 3 ? 'text-cpx-light-blue' : ''}>Your Bet</span>
            <span className={step === 4 ? 'text-cpx-light-blue' : ''}>Launch Pool</span>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderStep()}
          </motion.div>
        </form>
      </div>

      {/* Good Examples Modal */}
      {showExamplesModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#001233] border border-[#2AAAEA]/30 rounded-2xl p-8 max-w-md w-full shadow-2xl shadow-[#2AAAEA]/20"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-black text-white">✅ Good Pool Examples</h3>
              <button
                onClick={() => setShowExamplesModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-3">
              <div className="bg-[#009245]/10 border border-[#009245]/30 p-4 rounded-lg">
                <p className="text-white font-medium">"Will Tesla stock hit $300 by March 2025?"</p>
                <p className="text-xs text-gray-400 mt-2">✓ Specific price, clear date, verifiable</p>
              </div>
              <div className="bg-[#009245]/10 border border-[#009245]/30 p-4 rounded-lg">
                <p className="text-white font-medium">"Will Taylor Swift release an album in 2025?"</p>
                <p className="text-xs text-gray-400 mt-2">✓ YES/NO answer, publicly verifiable</p>
              </div>
              <div className="bg-[#009245]/10 border border-[#009245]/30 p-4 rounded-lg">
                <p className="text-white font-medium">"Will Bitcoin exceed $100k by year end?"</p>
                <p className="text-xs text-gray-400 mt-2">✓ Clear metric, specific deadline</p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-6 text-center">
              💡 Notice: All examples are YES/NO questions with clear, verifiable outcomes
            </p>
          </motion.div>
        </div>
      )}

      {/* AI Resolution Modal */}
      {showResolutionModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#001233] border border-[#2AAAEA]/30 rounded-2xl p-8 max-w-md w-full shadow-2xl shadow-[#2AAAEA]/20">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">AI Resolution Process</h3>
              <button
                onClick={() => setShowResolutionModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3 text-sm text-gray-300">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-cpx-light-blue rounded-full mt-2 flex-shrink-0"></div>
                <span>Research outcome using 50+ data sources</span>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-cpx-light-blue rounded-full mt-2 flex-shrink-0"></div>
                <span>Cross-validate with multiple AI validators</span>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-cpx-light-blue rounded-full mt-2 flex-shrink-0"></div>
                <span>Resolve market and distribute winnings</span>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-cpx-light-blue rounded-full mt-2 flex-shrink-0"></div>
                <span>Complete process within 1 hour</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI ODDS REASONING MODAL */}
      {showOddsReasoningModal && validationResult?.aiOdds && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowOddsReasoningModal(false)}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#001233] border border-[#2AAAEA]/40 rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-black text-white">🎲 AI Odds Analysis</h2>
              <button onClick={() => setShowOddsReasoningModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Odds Display */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-green-500/10 p-4 rounded-lg border border-green-500/30 text-center">
                <div className="text-sm text-gray-400 mb-2">YES</div>
                <div className="text-4xl font-black text-green-400">
                  {Math.round((validationResult.aiOdds.oddsYes || 0.5) * 100)}%
                </div>
              </div>
              <div className="bg-red-500/10 p-4 rounded-lg border border-red-500/30 text-center">
                <div className="text-sm text-gray-400 mb-2">NO</div>
                <div className="text-4xl font-black text-red-400">
                  {Math.round((validationResult.aiOdds.oddsNo || 0.5) * 100)}%
                </div>
              </div>
            </div>

            {/* Confidence */}
            <div className="bg-[#052393]/20 p-4 rounded-lg border border-[#2AAAEA]/30 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">AI Confidence</span>
                <span className="text-xl font-bold text-white">{validationResult.aiOdds.confidence || 0}%</span>
              </div>
            </div>

            {/* Reasoning */}
            {validationResult.aiOdds.reasoning && (
              <div className="bg-[#001233] p-4 rounded-lg border border-[#052393]/40 mb-6">
                <h3 className="text-sm font-bold text-white mb-3">📊 AI Reasoning</h3>
                <p className="text-gray-300 text-sm whitespace-pre-line">{validationResult.aiOdds.reasoning}</p>
              </div>
            )}

            {/* Research Summary */}
            {validationResult.aiOdds.researchSummary?.keyFactors && validationResult.aiOdds.researchSummary.keyFactors.length > 0 && (
              <div className="bg-[#052393]/10 p-4 rounded-lg border border-[#2AAAEA]/20 mb-6">
                <h3 className="text-sm font-bold text-white mb-3">🔍 Key Factors</h3>
                <ul className="space-y-2">
                  {validationResult.aiOdds.researchSummary.keyFactors.map((factor: string, i: number) => (
                    <li key={i} className="text-gray-300 text-sm flex items-start">
                      <span className="text-[#2AAAEA] mr-2">•</span>
                      <span>{factor}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Sources */}
            {validationResult.aiOdds.sources && validationResult.aiOdds.sources.length > 0 && (
              <div className="bg-gray-900/50 p-4 rounded-lg">
                <h3 className="text-sm font-bold text-white mb-3">📚 Data Sources</h3>
                <div className="space-y-2">
                  {validationResult.aiOdds.sources.map((source: any, i: number) => (
                    <div key={i} className="text-sm">
                      <span className="text-gray-400">{source.type}:</span>
                      <span className="text-white ml-2">{source.source}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  )
}